import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown, Trash } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import { Device } from "../../../hooks/common/interface";
import DeleteDeviceModal from "../../modal/DeleteDeviceModal";
import { DEVICE_STATUSES, TABLE_IDS } from "../../../constants/common";
import { usePingAllDevices } from "../../../hooks/common/mutation";
import toast from "react-hot-toast";
import { cn } from "../../../utils/cn";

interface DevicesConfigTableProps {
  devices: Device[];
  setSelectedDeviceId: Dispatch<SetStateAction<number>>;
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const DevicesConfigTable = ({
  devices,
  setSelectedDeviceId,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
}: DevicesConfigTableProps) => {
  const { i18n, t } = useTranslation();

  const [isDeleteDeviceModalOpen, setIsDeleteDeviceModalOpen] = useState(false);
  const [selectedDeletedDeviceId, setSelectedDeletedDeviceId] = useState(0);

  const { mutateAsync: PingAllDevices, isPending: isPingAllDevicesLoading } =
    usePingAllDevices();

  const handleDeleteDevice = useCallback(async (deviceId: number) => {
    setIsDeleteDeviceModalOpen(true);
    setSelectedDeletedDeviceId(deviceId);
  }, []);

  const handlePingAllDevices = useCallback(async () => {
    try {
      await PingAllDevices();

      toast.success(t("pingedAllDevicesSuccessfully"));
    } catch (error) {
      toast.error(t("somethingWentWrong"));
    }
  }, [PingAllDevices, t]);

  const columns: ColumnDef<Device>[] = useMemo(
    () => [
      {
        accessorKey: "n_DeviceID",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("deviceId")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span
              onClick={() => setSelectedDeviceId(row.original.n_DeviceID)}
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.n_DeviceID}
            </span>
          );
        },
      },
      {
        accessorKey: "s_DeviceName_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("deviceNameEn")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span className="capitalize">{row.original.s_DeviceName_En}</span>
          );
        },
      },
      {
        accessorKey: "s_DeviceName_Ar",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("deviceNameAr")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span className="capitalize">{row.original.s_DeviceName_Ar}</span>
          );
        },
      },
      {
        accessorKey: "s_IPAddress",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("ipAddress")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_IPAddress}</span>;
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_BuildingName_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("building")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_BuildingName_En
                : row.original.s_BuildingName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_FRDeviceType_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("deviceType")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_FRDeviceType_En
                : row.original.s_FRDeviceType_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "S_KioskFRDeviceName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("frDevice")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_KioskFRDeviceName_En
                : row.original.s_KioskFRDeviceName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_Status_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("status")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          if (!row.original.s_Status_En) return;
          return (
            <div
              className={cn(
                "text-center rounded-lg py-0.5 mx-auto max-w-[128px] w-[128px]",
                row.original.status === DEVICE_STATUSES.ONLINE
                  ? "bg-green-600"
                  : "bg-red-500",
              )}
            >
              <p className="text-sm text-white whitespace-normal">
                {i18n.language === "en"
                  ? row.original.s_Status_En
                  : row.original.s_Status_Ar}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "s_Description",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("description")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_Description}</span>;
        },
      },
      // S_MusterPointName_En
      {
        accessorKey: "S_MusterPointName_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("musterPoint")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_MusterPointName_En
                : row.original.s_MusterPointName_Ar}
            </span>
          );
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Trash
              onClick={() => handleDeleteDevice(row.original.n_DeviceID)}
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    [handleDeleteDevice, i18n.language, setSelectedDeviceId, t],
  );

  return (
    <>
      <TableWrapper
        data={devices}
        columns={columns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        tableId={TABLE_IDS.DEVICES_CONFIG}
        isSearch
        ButtonsWrapper={
          <Button
            type="button"
            onClick={handlePingAllDevices}
            isLoading={isPingAllDevicesLoading}
            className="bg-transparent ring-1 ring-purple-primary dark:ring-white dark:text-white text-purple-primary hover:bg-transparent "
          >
            {t("pingAllDevices")}
          </Button>
        }
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <DeleteDeviceModal
        deviceId={selectedDeletedDeviceId}
        isOpen={isDeleteDeviceModalOpen}
        onOpenChange={() => setIsDeleteDeviceModalOpen(false)}
      />
    </>
  );
};

export default React.memo(DevicesConfigTable);
